import React, { useState, useEffect, useContext } from 'react';
import { Grid, Typography, Toolbar, IconButton, Divider, Tooltip, Button, Select, MenuItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/DeleteSharp';
import CancelIcon from '@material-ui/icons/CancelSharp';
import CopyIcon from '@material-ui/icons/FileCopySharp';
import FolderIcon from '@material-ui/icons/FolderOpen';
import SeparatorIcon from '@material-ui/icons/ChevronRight';
import CheckBoxCheckedIcon from '@material-ui/icons/CheckBox';
import CheckBoxUncheckedIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIndeterminateIcon from '@material-ui/icons/IndeterminateCheckBox';
import SortAscIcon from '@material-ui/icons/ArrowUpwardSharp';
import SortDescIcon from '@material-ui/icons/ArrowDownwardSharp';

import { materialSrl } from '../utils/formatAppLib';
import { folderDisplayName, folderMatches } from '../utils/formatLib';
import { StitchIcon } from './Icons';
import { AppContext } from './AppState';
import DropdownWithTextField from './DropdownWithTextField';
import TagList from './TagList';
import { getCellHeader, getComparator, stableSort } from '../utils/tableLib'
import { copySnippet } from '../utils/uiLib';

const useStyles = makeStyles((theme) => ({
  divider: {
    height: '24px',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  iconButton: {
    marginLeft: theme.spacing(-0.5),
    marginRight: theme.spacing(-0.5),
  },
  selectBar: {
    display: 'flex',
    alignItems: 'center',
  },
  folderPart: {
    cursor: 'pointer',
    border: '1px solid grey'
  },
  folderPartFinal: {
    fontWeight: 'bold',
  },
  folderButton: {
    '&:hover': {
      backgroundColor: theme.palette.accent.main,
    },
  },
  folderButtonNotClickable: {
    cursor: 'default',
    '&:hover': {
      backgroundColor: '#ffffff00',
    },
  },
  folder: {
    height: '22px',
    paddingRight: theme.spacing(1),
  },
  folderSmall: {
    height: '16px',
    paddingRight: theme.spacing(1),
  },
  sortLabel: {
    margin: theme.spacing(1),
  },
  moveText: {
    color: theme.palette.secondary.light,
    paddingLeft: theme.spacing(1),
  },
  toggleButton: {
    marginLeft: theme.spacing(1.5),
    marginRight: theme.spacing(2),
    height: '32px',
    fontSize: '0.9em',
  },
  checkBox: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(-0.5),
    color: theme.palette.secondary.light,
  },
  checkBoxHighlighted: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(-0.5),
    color: theme.palette.primary.main,
  },
  padRight: {
    marginRight: theme.spacing(1),
  },
  selectionTooltip: {
    fontSize: '0.8em',
  },
}));


export default function SelectionToolbar(props) {
  const {
    inListView, materials, setShowStitchModal, folder, setFolder, folders, onMoveFolder, onTagsChanged,
    order, setOrder, orderBy, setOrderBy, handleOrderByChange, sortKeys,
    ...toolbarProps
  } = props;
  const classes = useStyles();

  const { userStitchingAvailable, qaDisplay, selected, setSelected, showAlert, userTags } = useContext(AppContext);
  const [folderParts, setFolderParts] = useState(['']);
  const [anchorEl, setAnchorEl] = useState(null);

  const numSelected = selected.length;

  useEffect(() => {
    setFolderParts(folderDisplayName(folder).split('/'));
  }, [folder]);

  const selectedMats = materials.filter(m => selected.includes(m.materialId));
  const visibleMats = materials ? materials.filter(m => m.visible && folderMatches(m.folder, folder)) : [];

  var allTags = [];
  for (var mat of selectedMats) {
    allTags.push(...(mat['tags.user'] ?? []));
  }
  var setAll = [];
  var setSome = [];
  const uniqueTags = [...new Set(allTags)];
  for (var tag of uniqueTags) {
    if (allTags.filter((t) => (t === tag)).length === selectedMats.length) {
      setAll.push(tag);
    } else {
      setSome.push(tag);
    }
  }
  const taggedAll = setAll.map(t => {return {label: t, tagType: 'user'}});
  const taggedSome = setSome.map(t => {return {label: t, tagType: 'user'}});

  function onClickFolderPart(n) {
    if (n === 0) {
      setFolder('');
    } else {
      var partsToJoin = [];
      for (var i = 1; i <= n; i++) {
        partsToJoin.push(folderParts(i));
      }
      setFolder(partsToJoin.join(''));
    }
  }

  function handleSelectAll(event) {
    if (numSelected < visibleMats.length) {
      setSelected(visibleMats.map(m => m.materialId));
    } else {
      setSelected([]);
    }
  }

  function getSelectedSrls() {
    const sortedSelectedMats = stableSort(selectedMats, getComparator(order, orderBy));
    return sortedSelectedMats.map(m => materialSrl(m.materialId)).join(' ');
  }

  function makeMenuItems(folders) {
    if (!folders) {
      return [];
    }
    var allFolders = folders;
    // Ensure it's always possible to move materials to root folder
    if (!allFolders.includes('')) {
      allFolders.unshift('');
    }
    return allFolders.map((folder, index) => {
      return ([
        <img
          key={`${index}-icon`}
          src='folder.512x410.png'
          className={classes.folderSmall}
        />,
        <Typography
          variant='body2'
          key={`${index}-name`}
        >
          {folderDisplayName(folder)}
        </Typography>
      ]);
    });
  }

  const button = (
    <Tooltip title='Move the selected materials'>
      <IconButton
        className={classes.iconButton}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <FolderIcon />
      </IconButton>
    </Tooltip>
  );

  const newItemPrefix = (<>
    <img
      src='folder.512x410.png'
      className={classes.folderSmall}
    />
    <Typography variant='body2'>Materials/</Typography>
  </>);

  const heading = (
    <Typography variant='body2' className={classes.moveText}>
      Moving {numSelected} selected material{numSelected > 1 ? 's' : ''} to folder
    </Typography>
  );

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
      }}
      {...toolbarProps}
    >
      <Grid container className={classes.selectBar}>
        <Grid item>
          <Grid container className={classes.selectBar}>
            <img
              src='folder.512x410.png'
              className={classes.folder}
            />
            {folderParts.map((f, i) => {
              return (<React.Fragment key={i}>
                {(i !== 0) && (
                  <SeparatorIcon />
                )}
                <Grid item>
                {(i === folderParts.length - 1) ? (
                  <Button className={classes.folderButtonNotClickable}>
                    <Typography className={classes.folderPartFinal}>
                      {f}
                    </Typography>
                  </Button>
                ) : (
                  <Button className={classes.folderButton} onClick={() => onClickFolderPart(i)}>
                    {f}
                  </Button>
                )}
                </Grid>
              </React.Fragment>);
            })}
          </Grid>
        </Grid>
        {!inListView && (<>
          <Grid item>
            <Divider className={classes.divider} orientation='vertical' />
          </Grid>
          <Grid item className={classes.sortLabel}>
            <Typography>
              Sort by
            </Typography>
          </Grid>
          <Grid item>
            <Select
              value={orderBy}
              onChange={handleOrderByChange}
            >
              {sortKeys.map((key) => (
                <MenuItem key={key} value={key}>
                  {getCellHeader(key)}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item>
            <IconButton
              className={classes.iconButton}
              onClick={(event) => {setOrder((prevOrder) => prevOrder === 'asc' ? 'desc' : 'asc')}}
            >
              {order === 'asc' ? (
                <SortAscIcon />
              ) : (
                <SortDescIcon />
              )}
            </IconButton>
          </Grid>
        </>)}
        {!inListView && (<>
          <Grid item>
            <Divider className={classes.divider} orientation='vertical' />
          </Grid>
          <Grid item>
            <Button
              onClick={handleSelectAll}
              variant='outlined'
              className={classes.toggleButton}
            >
              Select all
              {numSelected === 0 ? (
                <CheckBoxUncheckedIcon className={classes.checkBox} />
              ) : (
                numSelected === materials.length ? (
                  <CheckBoxCheckedIcon className={classes.checkBoxHighlighted} />
                ) : (
                  <CheckBoxIndeterminateIcon className={classes.checkBox} />
                )
              )}
            </Button>
          </Grid>
        </>)}
        {numSelected > 0 && (<>
          {inListView && (
            <Grid item className={classes.padRight}>
              <Divider className={classes.divider} orientation='vertical' />
            </Grid>
          )}
          <Grid item>
            <Tooltip
              classes={{ tooltip: classes.selectionTooltip }}
              title={selectedMats.map(m => m.name).join(', ')}
            >
              <Typography>
                {numSelected} selected
              </Typography>
            </Tooltip>
          </Grid>
          <Grid item>
            <IconButton
              className={classes.iconButton}
              onClick={() => {setSelected([])}}
            >
              <CancelIcon />
            </IconButton>
          </Grid>
          <Grid item>
            <Divider className={classes.divider} orientation='vertical' />
          </Grid>
        </>)}

        {numSelected > 0 && (<>
          {qaDisplay && (
            <Tooltip title='Copy material SRLs as a space separated list'>
              <div>  {/* A div is needed for the tooltip to fire even if the button is disabled */}
                <IconButton
                  className={classes.iconButton}
                  onClick={() => {copySnippet(getSelectedSrls(), showAlert);}}
                >
                  <CopyIcon />
                </IconButton>
              </div>
            </Tooltip>
          )}
          <TagList
            iconOnly={true}
            iconClass={classes.iconButton}
            iconTooltip='Set tags on the selected materials'
            tags={taggedAll}
            indeterminate={taggedSome}
            onChange={onTagsChanged}
            tagChoices={userTags}
          />
          {/* Folder button */}
          <DropdownWithTextField
            items={makeMenuItems(folders)}
            values={folders}
            newItemPrefix={newItemPrefix}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            button={button}
            heading={heading}
            onSelect={onMoveFolder}
          />
          {(userStitchingAvailable || qaDisplay) && (
            <Tooltip title='Combine overlapping materials into one large stitched material'>
              <div>  {/* A div is needed for the tooltip to fire even if the button is disabled */}
                <IconButton
                  className={classes.iconButton}
                  disabled={numSelected < 2}
                  onClick={() => {setShowStitchModal(true);}}
                >
                  <StitchIcon />
                </IconButton>
              </div>
            </Tooltip>
          )}
          {/* <Tooltip title='Delete the selected materials'>
            <IconButton className={classes.iconButton}>
              <DeleteIcon />
            </IconButton>
          </Tooltip> */}
        </>)}
      </Grid>
    </Toolbar>
  );
}
